import {
  addDays,
  differenceInCalendarDays,
  differenceInDays,
  differenceInSeconds,
  endOfMonth,
  formatDistanceToNowStrict,
  formatDuration,
  getDate,
  getMonth,
  getYear,
  intervalToDuration,
  isBefore,
  isValid,
  subMonths,
  subWeeks,
} from 'date-fns';
import { toZonedTime, formatInTimeZone } from 'date-fns-tz';
import humanizeDuration from 'humanize-duration';
import i18n from 'i18next';

/**
 * Gets the user's current timezone.
 * This is a convenience wrapper around the native JavaScript API.
 *
 * @returns {string} - The user's timezone (e.g., "America/New_York", "Europe/London").
 *
 * @example
 * ```typescript
 * getUserTimezone() // "America/New_York"
 * ```
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Gets the current timezone offset for the user's timezone.
 * Returns the offset in UTC format (e.g., "UTC-5", "UTC+3").
 *
 * @returns {string} - The timezone offset in UTC format (e.g., "UTC-5", "UTC+3").
 *
 * @example
 * ```typescript
 * getCurrentTimezoneOffset() // "UTC-5" (Eastern Time)
 * getCurrentTimezoneOffset() // "UTC+1" (Central European Time)
 * ```
 */
export const getCurrentTimezoneOffset = (): string => {
  try {
    const date = new Date();
    const offsetMinutes = date.getTimezoneOffset();
    const offsetHours = Math.abs(offsetMinutes) / 60;
    const sign = offsetMinutes > 0 ? '-' : '+';

    return `UTC${sign}${offsetHours}`;
  } catch {
    // Fallback to UTC+0 if calculation fails
    return 'UTC+0';
  }
};

/**
 * Formats a date in a specific timezone with the given pattern.
 * This is a wrapper around date-fns-tz formatInTimeZone for consistency.
 *
 * @param {Date} date - The date to format
 * @param {string} timezone - The timezone to format in (e.g., 'UTC', 'America/New_York')
 * @param {string} pattern - The format pattern (e.g., 'MMMM d, yyyy', 'h:mm:ss a')
 * @returns {string} - The formatted date string, or empty string if invalid
 *
 * @example
 * ```typescript
 * formatDateInTimezone(new Date(), 'UTC', 'MMMM d, yyyy') // "January 15, 2024"
 * formatDateInTimezone(new Date(), 'America/New_York', 'h:mm:ss a') // "10:00:00 AM"
 * ```
 */
export const formatDateInTimezone = (date: Date, timezone: string, pattern: string): string => {
  try {
    return formatInTimeZone(date, timezone, pattern);
  } catch {
    return '';
  }
};

/**
 * Helper function to parse date strings with proper timezone handling.
 *
 * **Timezone Handling Rules:**
 * - Date-only strings (YYYY-MM-DD): Treated as local timezone
 * - ISO datetime without timezone: Assumed to be UTC (API timestamps are typically UTC)
 * - ISO datetime with timezone: Timezone respected
 * - Numeric timestamps: Already UTC
 *
 * @param {string | Date | number} date - The date string, Date object, or timestamp to parse
 * @returns {Date | null} - Date object or null if invalid
 *
 * @example
 * ```typescript
 * parseTimestamp('2023-12-01') // Date in local timezone
 * parseTimestamp('2023-12-01T10:30:00') // Treated as UTC
 * parseTimestamp('2023-12-01T10:30:00Z') // Already UTC
 * parseTimestamp(1704067200000) // UTC timestamp
 * parseTimestamp('invalid-date') // null
 * ```
 */
export const parseTimestamp = (date: string | Date | number): Date | null => {
  // Handle Date objects
  if (date instanceof Date) {
    return isValid(date) ? date : null;
  }

  // Handle numeric timestamps (milliseconds since epoch)
  if (typeof date === 'number') {
    const dateObj = new Date(date);
    if (!isValid(dateObj)) {
      return null;
    }

    // The Date constructor already interprets numeric timestamps as UTC.
    return dateObj;
  }

  // Handle string dates
  if (typeof date === 'string') {
    try {
      // Handle date-only strings (YYYY-MM-DD) - create in local timezone
      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = date.split('-').map(Number);
        const dateObj = new Date(year, month - 1, day); // month is 0-indexed
        return isValid(dateObj) ? dateObj : null;
      }

      // Handle ISO datetime strings - assume UTC if no timezone specified
      // This ensures consistent behavior across logs, chat, and all timestamp displays
      if (date.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/)) {
        // Check if timezone info is already present
        const hasTimezone = /Z|[+-]\d{2}:?\d{2}$/.test(date);
        const dateObj = new Date(date + (hasTimezone ? '' : 'Z'));
        return isValid(dateObj) ? dateObj : null;
      }

      // Handle all other formats (with timezone, ISO strings, etc.)
      const dateObj = new Date(date);
      return isValid(dateObj) ? dateObj : null;
    } catch {
      return null;
    }
  }
};

/**
 * Formats a date to a string using the specified format.
 * Default format is 'yyyy-MM-dd' for consistency across the application.
 *
 * **IMPORTANT:** For ISO datetime strings without timezone info (e.g., "2023-12-01T10:00:00"),
 * this function assumes UTC timezone and converts to the user's local timezone for display.
 * This ensures consistent behavior across the application.
 *
 * @param {string | Date | number} date - The date to format. Can be a date string, Date object, or timestamp.
 * @param {string} [formatString='yyyy-MM-dd'] - The format string to use.
 * @returns {string} - The formatted date string in user's local timezone, or empty string if invalid.
 *
 * @example
 * ```typescript
 * formatDate('2023-12-01T10:00:00Z') // "2023-12-01" (already has timezone)
 * formatDate('2023-12-01T10:00:00') // "2023-12-01" (assumes UTC, converts to local)
 * formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss') // "2023-12-01 10:00:00"
 * formatDate(1704067200000) // "2024-01-01"
 * formatDate('invalid-date') // ""
 * ```
 */
export const formatDate = (
  date: string | Date | number,
  formatString: string = 'yyyy-MM-dd'
): string => {
  const dateObj = parseTimestamp(date);
  if (!dateObj) {
    return '';
  }

  try {
    // Convert to user's timezone for display
    const userTimezone = getUserTimezone();
    return formatInTimeZone(dateObj, userTimezone, formatString);
  } catch {
    return '';
  }
};

/**
 * Returns true when two timestamps fall in the same wall-clock minute (HH:mm)
 * in the user's timezone. Used by replay parsers to coalesce events that
 * arrived within the same minute boundary.
 */
export const isSameMinute = (a: string | Date | number, b: string | Date | number): boolean =>
  formatDate(a, 'HH:mm') === formatDate(b, 'HH:mm');

/**
 * Converts a timestamp to a time string showing how much time has passed (e.g., "2 hours ago", "3 days ago").
 * This function handles various timestamp formats including strings, Date objects, and numeric timestamps.
 *
 * @param {string | Date | number} value - The timestamp to convert. Can be an ISO string, Date object, or numeric timestamp.
 * @returns {string} - The formatted time string, or empty string if invalid.
 *
 * @example
 * ```typescript
 * timeFromNow('2024-08-08T10:30:00Z') // "3 hours ago"
 * timeFromNow(1723123456789) // "2 days ago"
 * timeFromNow(new Date()) // "less than a minute ago"
 * ```
 */
export const timeFromNow = (value: string | Date | number): string => {
  try {
    let dateObj: Date;

    if (typeof value === 'number') {
      // Handle numeric timestamps (milliseconds since epoch)
      dateObj = new Date(value);
    } else if (typeof value === 'string') {
      // Use the safe parser for string dates
      const parsed = parseTimestamp(value);
      if (!parsed) {
        return '';
      }
      dateObj = parsed;
    } else {
      // Handle Date objects
      dateObj = value;
    }

    if (!isValid(dateObj)) {
      return '';
    }

    // Render timestamps that are less than a second old as "just now" to avoid "in 0 seconds" text.
    //
    // Math.abs() is intentional because this function supports
    // both past ("ago") and future ("in X") timestamps.
    const elapsed = new Date().getTime() - dateObj.getTime();
    if (Math.abs(elapsed) < 1000) {
      return i18n.t('common.relative_time.just_now');
    }

    // Convert to user's timezone before calculating relative time
    const userTimezone = getUserTimezone();
    const zonedDate = toZonedTime(dateObj, userTimezone);

    return formatDistanceToNowStrict(zonedDate, { addSuffix: true });
  } catch {
    return '';
  }
};

/**
 * Checks if two dates are the same day.
 *
 * @param {string | Date | number} date1 - The first date to compare.
 * @param {string | Date | number} date2 - The second date to compare.
 * @returns {boolean} - True if both dates are the same day, false otherwise.
 *
 * @example
 * ```typescript
 * isSameDay('2023-12-01', '2023-12-01') // true
 * isSameDay('2023-12-01', '2023-12-02') // false
 * isSameDay(1704067200000, new Date('2024-01-01')) // true
 * ```
 */
export const isSameDay = (
  date1: string | Date | number,
  date2: string | Date | number
): boolean => {
  const date1Obj = parseTimestamp(date1);
  const date2Obj = parseTimestamp(date2);

  if (!date1Obj || !date2Obj) {
    return false;
  }

  try {
    // Compare year, month, and day components directly
    return (
      getYear(date1Obj) === getYear(date2Obj) &&
      getMonth(date1Obj) === getMonth(date2Obj) &&
      getDate(date1Obj) === getDate(date2Obj)
    );
  } catch {
    return false;
  }
};

/**
 * Subtracts a specified number of months from the current date.
 *
 * @param {number} months - The number of months to subtract.
 * @returns {Date} - The resulting date.
 *
 * @example
 * ```typescript
 * subMonthsFromNow(1) // Date object for 1 month ago
 * subMonthsFromNow(3) // Date object for 3 months ago
 * ```
 */
export const subMonthsFromNow = (months: number): Date => {
  return subMonths(new Date(), months);
};

/**
 * Subtracts a specified number of weeks from the current date.
 *
 * @param {number} weeks - The number of weeks to subtract.
 * @returns {Date} - The resulting date.
 *
 * @example
 * ```typescript
 * subWeeksFromNow(1) // Date object for 1 week ago
 * subWeeksFromNow(2) // Date object for 2 weeks ago
 * ```
 */
export const subWeeksFromNow = (weeks: number): Date => {
  return subWeeks(new Date(), weeks);
};

/**
 * Checks if a date is older than a specified number of months from now.
 * This function handles various date formats including strings, Date objects, and numeric timestamps.
 *
 * @param {string | Date | number} date - The date to check. Can be an ISO string, Date object, or numeric timestamp.
 * @param {number} months - The number of months to compare against.
 * @returns {boolean} - True if the date is older than the specified months, false otherwise.
 *
 * @example
 * ```typescript
 * isOlderThan('2023-01-01', 6) // true (if current date is after 2023-07-01)
 * isOlderThan(new Date(), 6) // false
 * isOlderThan(1704067200000, 1) // true (if current date is after 2024-02-01)
 * ```
 */
export const isOlderThan = (date: string | Date | number, months: number): boolean => {
  const parsedDate = parseTimestamp(date);
  if (!parsedDate) {
    return false;
  }

  const cutoffDate = subMonthsFromNow(months);
  return isBefore(parsedDate, cutoffDate);
};

/**
 * Adds a specified number of days to a date.
 * This function handles various date formats including strings, Date objects, and numeric timestamps.
 *
 * @param {string | Date | number} date - The date to add days to. Can be an ISO string, Date object, or numeric timestamp.
 * @param {number} days - The number of days to add (can be negative to subtract days).
 * @returns {Date} - The resulting date, or null if the input date is invalid.
 *
 * @example
 * ```typescript
 * addDaysToDate('2023-12-01', 1) // Date object for 2023-12-02
 * addDaysToDate(new Date(), -1) // Date object for yesterday
 * addDaysToDate(1704067200000, 7) // Date object for 7 days after the timestamp
 * ```
 */
export const addDaysToDate = (date: string | Date | number, days: number): Date | null => {
  const parsedDate = parseTimestamp(date);
  if (!parsedDate) {
    return null;
  }

  try {
    return addDays(parsedDate, days);
  } catch {
    return null;
  }
};

/**
 * Computes the formatted start-of-day bound for a session content query.
 * Subtracts one day from the given date and formats it as 'yyyy-MM-dd'.
 */
export const computeSessionBoundDate = (sessionStart: string): string | undefined => {
  const startDay = addDaysToDate(sessionStart, -1);
  return startDay ? formatDate(startDay) : undefined;
};

/**
 * Calculates the difference in days between two dates.
 * Returns the number of days between the end date and start date.
 *
 * @param {string | Date | number} end - The end date
 * @param {string | Date | number} start - The start date
 * @returns {number} - The number of days difference (can be negative if end is before start)
 *
 * @example
 * ```typescript
 * getDaysDifference('2024-01-15', '2024-01-10') // 5
 * getDaysDifference('2024-01-10', '2024-01-15') // -5
 * getDaysDifference(new Date('2024-01-15'), new Date('2024-01-10')) // 5
 * ```
 */
export const getDaysDifference = (
  end: string | Date | number,
  start: string | Date | number
): number => {
  const endDate = parseTimestamp(end);
  const startDate = parseTimestamp(start);

  if (!endDate || !startDate) {
    return 0;
  }

  return differenceInDays(endDate, startDate);
};

/**
 * Calculates the difference in seconds between two dates
 * @param end - The end date
 * @param start - The start date
 * @returns The number of seconds between the dates, or 0 if either date is invalid
 */
export const getSecondsDifference = (
  end: string | Date | number,
  start: string | Date | number
): number => {
  const endDate = parseTimestamp(end);
  const startDate = parseTimestamp(start);

  if (!endDate || !startDate) {
    return 0;
  }

  return differenceInSeconds(endDate, startDate);
};

/**
 * Gets the last day of the month for a given date.
 * This function handles various date formats including strings, Date objects, and numeric timestamps.
 *
 * @param {string | Date | number} date - The date to get the end of month for. Can be an ISO string, Date object, or numeric timestamp.
 * @returns {Date} - The last day of the month, or null if the input date is invalid.
 *
 * @example
 * ```typescript
 * endOfMonth('2023-12-15') // Date object for 2023-12-31
 * endOfMonth(new Date('2024-02-10')) // Date object for 2024-02-29 (leap year)
 * endOfMonth(1704067200000) // Date object for end of month of the timestamp
 * ```
 */
export const getEndOfMonth = (date: string | Date | number): Date | null => {
  const parsedDate = parseTimestamp(date);
  if (!parsedDate) {
    return null;
  }

  try {
    // For timestamps, work with UTC to avoid timezone issues
    if (typeof date === 'number') {
      const utcDate = new Date(date);
      const year = utcDate.getUTCFullYear();
      const month = utcDate.getUTCMonth();
      // Get the last day of the month in UTC
      const lastDay = new Date(Date.UTC(year, month + 1, 0));
      return new Date(Date.UTC(year, month, lastDay.getUTCDate()));
    }

    // For other types, use the existing logic
    const endOfMonthDate = endOfMonth(parsedDate);
    return new Date(
      Date.UTC(endOfMonthDate.getFullYear(), endOfMonthDate.getMonth(), endOfMonthDate.getDate())
    );
  } catch {
    return null;
  }
};

// Humanizer for the full, spelled-out relative time used in rich tooltips
// (e.g. "19 hours, 15 minutes, 39 seconds"). Uses humanize-duration's built-in
// localized dictionaries, so unit words and pluralization are handled per language.
const detailedHumanizer = humanizeDuration.humanizer({
  units: ['y', 'mo', 'd', 'h', 'm', 's'],
  largest: 3,
  round: true,
  fallbacks: ['en'],
});

// Maps an i18next language code to the language code humanize-duration expects.
// humanize-duration uses 'pt' (not 'pt-br'); an unmapped code would silently fall
// back to English, which is exactly the bug we are fixing.
const toHumanizeLanguage = (language: string | undefined): string => {
  const normalized = (language ?? '').toLowerCase();
  if (normalized.startsWith('es')) {
    return 'es';
  }
  if (normalized.startsWith('pt')) {
    return 'pt';
  }
  if (normalized.startsWith('el')) {
    return 'el';
  }
  return 'en';
};

const MINUTE_MS = 60_000;

// Shared skeleton; variants differ only in humanizer, language, just-now threshold/label, sub-minute label, and suffix.
const formatRelativeTimeFromNow = (
  value: string | Date | number,
  humanizer: ReturnType<typeof humanizeDuration.humanizer>,
  language: string,
  {
    withSuffix = true,
    justNowThresholdMs = 1000,
    justNowLabel,
    subMinuteLabel,
  }: {
    withSuffix?: boolean;
    justNowThresholdMs?: number;
    justNowLabel?: string;
    subMinuteLabel?: string;
  } = {}
): string => {
  try {
    const dateObj = parseTimestamp(value);
    if (!dateObj) {
      return '';
    }

    const elapsed = new Date().getTime() - dateObj.getTime();
    const absMs = Math.abs(elapsed);
    if (absMs < justNowThresholdMs) {
      return justNowLabel ?? i18n.t('common.relative_time.just_now');
    }

    const core =
      subMinuteLabel && absMs < MINUTE_MS ? subMinuteLabel : humanizer(absMs, { language });
    if (!withSuffix) {
      return core;
    }
    // Future timestamps read "in <duration>" instead of the past-tense "ago" suffix.
    if (elapsed < 0) {
      return i18n.t('common.relative_time.in_short', { 0: core });
    }
    return `${core} ${i18n.t('common.relative_time.ago_short')}`;
  } catch {
    return '';
  }
};

/**
 * Converts a timestamp to a detailed time string showing how much time has passed with maximum precision.
 * Shows up to 3 significant units (years, months, days, hours, minutes, seconds) in descending order.
 *
 * @param {string | Date | number} value - The timestamp to convert. Can be an ISO string, Date object, or numeric timestamp.
 * @returns {string} - The detailed formatted time string, or empty string if invalid.
 *
 * @example
 * ```typescript
 * timeFromNowDetailed('2024-08-08T10:30:00Z') // "12 days, 3 hours, 20 minutes ago"
 * timeFromNowDetailed(1723123456789) // "2 years, 3 months, 15 days ago"
 * timeFromNowDetailed(new Date()) // "45 seconds ago"
 * ```
 */
export const timeFromNowDetailed = (value: string | Date | number): string =>
  formatRelativeTimeFromNow(value, detailedHumanizer, toHumanizeLanguage(i18n.language));

/**
 * Formats a duration in seconds to a clock string: `mm:ss`, upgrading to
 * `h:mm:ss` once it reaches an hour (e.g. "00:32", "1:04:09").
 *
 * @param seconds - Total duration in seconds (can be fractional; floored per unit)
 * @returns A `mm:ss` string under an hour, `h:mm:ss` at or over an hour
 */
export const formatSecondsToMmSs = (seconds: number): string => {
  const totalSeconds = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  if (hours > 0) {
    return `${hours}:${pad(minutes)}:${pad(remainingSeconds)}`;
  }
  return `${pad(minutes)}:${pad(remainingSeconds)}`;
};

/**
 * Formats a date to show only the time in 12-hour format (h:mm AM/PM).
 *
 * @param {string | Date | number} date - The date to format. Can be a date string, Date object, or timestamp.
 * @returns {string} - The formatted time string in 12-hour format (h:mm AM/PM), or empty string if invalid.
 *
 * @example
 * ```typescript
 * formatTimeOnly('2023-12-01T10:30:00Z') // "10:30 AM"
 * formatTimeOnly(new Date()) // "2:45 PM"
 * formatTimeOnly(1704067200000) // "12:00 PM"
 * formatTimeOnly('invalid-date') // ""
 * ```
 */
export const formatTimeOnly = (date: string | Date | number): string => {
  const dateObj = parseTimestamp(date);
  if (!dateObj) {
    return '';
  }

  try {
    const userTimezone = getUserTimezone();
    return formatInTimeZone(dateObj, userTimezone, 'h:mm a');
  } catch {
    return '';
  }
};

/**
 * Converts minutes to seconds.
 * Used for backward compatibility with legacy reminder data that uses minutes.
 *
 * @param {number} minutes - The number of minutes to convert
 * @returns {number} - The equivalent number of seconds
 *
 * @example
 * ```typescript
 * convertMinutesToSeconds(5) // 300
 * convertMinutesToSeconds(1) // 60
 * convertMinutesToSeconds(0.5) // 30
 * ```
 */
export const convertMinutesToSeconds = (minutes: number): number => {
  return Math.floor(minutes * 60);
};

/**
 * Formats a duration in seconds to a human-readable string.
 * Uses date-fns formatDuration with i18n support for multiple languages (en, es, pt-br).
 * Automatically selects the most appropriate unit (seconds, minutes, hours, or days).
 *
 * @param {number} seconds - The number of seconds to format
 * @returns {string} - A formatted string like "10 seconds", "5 minutes", "2 hours", or "1 day"
 *
 * @example
 * ```typescript
 * // English
 * formatSecondsToReadable(10) // "10 seconds"
 * formatSecondsToReadable(60) // "1 minute"
 * formatSecondsToReadable(300) // "5 minutes"
 * // Spanish
 * formatSecondsToReadable(60) // "1 minuto"
 * formatSecondsToReadable(300) // "5 minutos"
 * // Portuguese (Brazil)
 * formatSecondsToReadable(3600) // "1 hora"
 * formatSecondsToReadable(86400) // "1 dia"
 * ```
 */
export const formatSecondsToReadable = (seconds: number): string => {
  const duration = intervalToDuration({ start: 0, end: seconds * 1000 });
  return formatDuration(duration, { format: ['days', 'hours', 'minutes', 'seconds'], zero: false });
};

// Custom humanizer with abbreviated units for supported locales
const shortHumanizer = humanizeDuration.humanizer({
  spacer: '',
  units: ['y', 'mo', 'w', 'd', 'h', 'm', 's'],
  largest: 1,
  round: true,
  fallbacks: ['en'],
  languages: {
    en: {
      y: () => 'y',
      mo: () => 'mo',
      w: () => 'w',
      d: () => 'd',
      h: () => 'h',
      m: () => 'm',
      s: () => 's',
    },
    es: {
      y: () => 'a',
      mo: () => 'me',
      w: () => 'se',
      d: () => 'd',
      h: () => 'h',
      m: () => 'm',
      s: () => 's',
    },
    'pt-br': {
      y: () => 'a',
      mo: () => 'me',
      w: () => 'se',
      d: () => 'd',
      h: () => 'h',
      m: () => 'm',
      s: () => 's',
    },
  },
});

/**
 * Converts a timestamp to an abbreviated relative time string (e.g., "3d ago", "5m ago").
 * Uses the current i18n locale for language-appropriate abbreviations.
 *
 * @param {string | Date | number} value - The timestamp to convert.
 * @returns {string} The abbreviated relative time string, or empty string if invalid.
 *
 * @example
 * ```typescript
 * timeFromNowShort('2024-08-08T10:30:00Z') // "3d ago"
 * timeFromNowShort(1723123456789) // "2d ago"
 * timeFromNowShort(new Date()) // "just now"
 * ```
 */
// Passes raw i18n.language (not toHumanizeLanguage): shortHumanizer's custom dicts
// are keyed by i18n codes ('pt-br'), so mapping 'pt-br'→'pt' would miss them.
export const timeFromNowShort = (value: string | Date | number): string =>
  formatRelativeTimeFromNow(value, shortHumanizer, i18n.language);

/** Compact relative time (Just now, <1m, 5m, 2h, 3w, 2mo); `withSuffix` adds the localized "ago". */
export const timeFromNowCompact = (
  value: string | Date | number,
  { withSuffix = false }: { withSuffix?: boolean } = {}
): string =>
  formatRelativeTimeFromNow(value, shortHumanizer, i18n.language, {
    withSuffix,
    justNowThresholdMs: 10_000,
    justNowLabel: i18n.t('common.just_now'),
    subMinuteLabel: i18n.t('common.less_than_1m'),
  });

/** Calendar-day bucket labels for relative-recency grouping (newest → oldest). */
export type DateBucket = 'today' | 'yesterday' | 'last_7_days' | 'last_30_days' | 'older';

export const DATE_BUCKET_ORDER: DateBucket[] = [
  'today',
  'yesterday',
  'last_7_days',
  'last_30_days',
  'older',
];

/** Shared i18n label key per date bucket (`activityLogs.buckets.*`). */
export const DATE_BUCKET_LABEL_KEY: Record<DateBucket, string> = {
  today: 'activityLogs.buckets.today',
  yesterday: 'activityLogs.buckets.yesterday',
  last_7_days: 'activityLogs.buckets.last_7_days',
  last_30_days: 'activityLogs.buckets.last_30_days',
  older: 'activityLogs.buckets.older',
};

/**
 * Buckets a date into Today / Yesterday / Last 7 days / Last 30 days / Older by
 * calendar day in the viewer's local timezone — NOT a rolling 24h window, so a
 * 2am and an 11pm event yesterday both bucket as "yesterday". `differenceInCalendarDays`
 * counts local midnight boundaries crossed (DST-safe). `now` is injectable for tests.
 */
export const getDateBucket = (
  value: string | Date | number,
  now: number | Date = Date.now()
): DateBucket => {
  const dayDiff = differenceInCalendarDays(now, new Date(value));
  if (dayDiff <= 0) {
    return 'today';
  }
  if (dayDiff === 1) {
    return 'yesterday';
  }
  if (dayDiff <= 7) {
    return 'last_7_days';
  }
  if (dayDiff <= 30) {
    return 'last_30_days';
  }
  return 'older';
};
